import { useState } from "react";
import { FONTS, COLOR_SWATCHES, BLEND_MODES } from "../../lib/constants";
import {
  TextAlignLeft, TextAlignCenter, TextAlignRight, TextB, TextItalic,
  Copy, Trash, ArrowUp, ArrowDown, TextT, Palette, Scissors, Spinner,
} from "@phosphor-icons/react";
import { Slider } from "../ui/slider";
import TextEffects from "./TextEffects";
import ImageEffects from "./ImageEffects";
import AdjustmentsPanel from "./AdjustmentsPanel";
import { CharacterPanel, ParagraphPanel } from "./TypographyPanels";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

function RemoveBgButton({ el, onUpdate, onStart }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    const tid = toast.loading("Removing background…");
    try {
      const res = await api.post("/images/remove-background", { image_url: el.src });
      onStart();
      onUpdate({ src: fileUrl(res.data.storage_path) });
      toast.success("Background removed", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove background", { id: tid });
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={run}
      disabled={loading}
      data-testid="remove-bg-btn"
      className="w-full mb-4 h-10 rounded-lg bg-gradient-to-r from-fuchsia-600 to-blue-600 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-opacity"
    >
      {loading ? <Spinner className="animate-spin" size={18} /> : <Scissors weight="fill" size={18} />}
      {loading ? "Removing…" : "Remove background"}
    </button>
  );
}

const Row = ({ label, children }) => (
  <div className="mb-4">
    <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">{label}</p>
    {children}
  </div>
);

function ColorField({ value, onChange, onStart, testid }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <label className="relative h-9 w-9 rounded-lg border border-white/15 overflow-hidden cursor-pointer shrink-0" style={{ background: value || "transparent" }}>
          <input
            type="color"
            value={value || "#000000"}
            onMouseDown={onStart}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid={testid}
          />
        </label>
        <input
          type="text"
          value={value || ""}
          onFocus={onStart}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-sm text-white outline-none focus:border-blue-500/60"
        />
      </div>
      <div className="grid grid-cols-8 gap-1.5">
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c}
            onClick={() => { onStart && onStart(); onChange(c); }}
            className="h-5 w-5 rounded border border-white/15 hover:scale-110 transition-transform"
            style={{ background: c }}
          />
        ))}
      </div>
    </div>
  );
}

const NumberInput = ({ value, onChange, onStart, testid }) => (
  <input
    type="number"
    value={Math.round(value)}
    onFocus={onStart}
    onChange={(e) => onChange(Number(e.target.value))}
    data-testid={testid}
    className="w-full h-9 rounded-lg bg-black/40 border border-white/10 px-2.5 text-sm text-white outline-none focus:border-blue-500/60"
  />
);

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-zinc-300">{label}</span>
    <button
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-white/15"}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-4" : "left-0.5"}`} />
    </button>
  </div>
);

export default function PropertiesPanel({ el, onUpdate, onStart, onCommit, onDuplicate, onDelete, onLayer, wordSel, onWordStyle, onWordSelect, onClearWordStyles }) {
  if (!el) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 text-zinc-500">
        <Palette size={34} className="mb-3 opacity-40" />
        <p className="text-sm">Select an element to edit its properties.</p>
      </div>
    );
  }

  const u = (patch) => onUpdate(patch);

  return (
    <div className="p-4 custom-scrollbar overflow-y-auto h-full" data-testid="properties-panel">
      <div className="flex items-center gap-2 mb-5">
        <button onClick={onDuplicate} data-testid="prop-duplicate" className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-medium transition-colors">
          <Copy size={15} /> Duplicate
        </button>
        <button onClick={() => onLayer("up")} data-testid="prop-forward" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" title="Bring forward">
          <ArrowUp size={15} />
        </button>
        <button onClick={() => onLayer("down")} data-testid="prop-backward" className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" title="Send backward">
          <ArrowDown size={15} />
        </button>
        <button onClick={onDelete} data-testid="prop-delete" className="h-9 w-9 rounded-lg bg-red-600/15 hover:bg-red-600/30 text-red-400 flex items-center justify-center transition-colors" title="Delete">
          <Trash size={15} />
        </button>
      </div>

      {el.type === "text" && (
        <>
          <div className="mb-4" data-testid="text-content-editor">
            <label className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold flex items-center gap-1.5 mb-1.5">
              <TextT size={13} weight="bold" /> Text content
            </label>
            <textarea
              value={el.text || ""}
              onFocus={onStart}
              onChange={(e) => u({ text: e.target.value })}
              onBlur={() => onCommit && onCommit()}
              onSelect={(e) => {
                const t = e.target;
                onWordSelect && onWordSelect(t.selectionEnd > t.selectionStart ? { elId: el.id, start: t.selectionStart, end: t.selectionEnd } : null);
              }}
              rows={3}
              placeholder="Type your text…"
              data-testid="text-content-input"
              className="w-full rounded-lg bg-black/40 border border-white/15 focus:border-blue-500/60 outline-none px-3 py-2 text-sm text-white resize-y leading-snug"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Tip: highlight word(s) above (or on the canvas), then pick a color or stroke to style just those words.</p>
          </div>
          <CharacterPanel el={el} onUpdate={u} onStart={onStart} wordSel={wordSel} onWordStyle={onWordStyle} onClearWordStyles={onClearWordStyles} />
          <div className="border-t border-white/10 my-4" />
          <ParagraphPanel el={el} onUpdate={u} onStart={onStart} />
          <div className="border-t border-white/10 my-4" />
          <TextEffects el={el} onUpdate={u} onStart={onStart} onApplyPreset={(patch) => { onStart(); u(patch); }} />
        </>
      )}

      {(el.type === "rect" || el.type === "ellipse" || el.type === "line") && (
        <>
          <Row label="Fill color">
            <ColorField value={el.fill} onStart={onStart} onChange={(v) => u({ fill: v })} testid="prop-fill-color" />
          </Row>
          {el.type !== "line" && (
            <>
              <Row label="Border color">
                <ColorField value={el.stroke} onStart={onStart} onChange={(v) => u({ stroke: v, strokeWidth: el.strokeWidth || 4 })} testid="prop-stroke-color" />
              </Row>
              <Row label="Border width">
                <Slider value={[el.strokeWidth || 0]} min={0} max={40} step={1} onPointerDown={onStart} onValueChange={(v) => u({ strokeWidth: v[0] })} />
              </Row>
            </>
          )}
          {el.type === "rect" && (
            <Row label="Corner radius">
              <Slider value={[el.borderRadius || 0]} min={0} max={200} step={2} onPointerDown={onStart} onValueChange={(v) => u({ borderRadius: v[0] })} />
            </Row>
          )}
        </>
      )}

      {el.type === "image" && (
        <>
          <RemoveBgButton el={el} onUpdate={onUpdate} onStart={onStart} />
          <ImageEffects el={el} onUpdate={u} onStart={onStart} />
          <div className="border-t border-white/10 my-4" />
          <Row label="Corner radius">
            <Slider value={[el.borderRadius || 0]} min={0} max={300} step={2} onPointerDown={onStart} onValueChange={(v) => u({ borderRadius: v[0] })} />
          </Row>
          <AdjustmentsPanel el={el} onUpdate={u} onStart={onStart} />
        </>
      )}

      {(el.type === "video" || el.type === "embed") && (
        <div className="mb-5 space-y-3" data-testid="media-controls">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Playback</p>
          <ToggleRow label="Loop" checked={el.loop !== false} onChange={(v) => u({ loop: v, ...(v ? { playOnce: false } : {}) })} testid="media-loop" />
          {el.type === "video" && (
            <ToggleRow label="Play once" checked={!!el.playOnce} onChange={(v) => u({ playOnce: v, ...(v ? { loop: false } : {}) })} testid="media-playonce" />
          )}
          <ToggleRow label="Autoplay" checked={el.autoplay !== false} onChange={(v) => u({ autoplay: v })} testid="media-autoplay" />
          <ToggleRow label="Mute" checked={el.muted !== false} onChange={(v) => u({ muted: v })} testid="media-mute" />
          {el.muted === false && (
            <Row label="Volume">
              <Slider value={[Math.round((el.volume ?? 1) * 100)]} min={0} max={100} step={1} onPointerDown={onStart} onValueChange={(v) => u({ volume: v[0] / 100 })} />
            </Row>
          )}
          {el.type === "video" && (
            <Row label="Corner radius">
              <Slider value={[el.borderRadius || 0]} min={0} max={300} step={2} onPointerDown={onStart} onValueChange={(v) => u({ borderRadius: v[0] })} />
            </Row>
          )}
          {el.type === "embed" && (
            <p className="text-[10px] text-zinc-500 break-all leading-relaxed pt-1">{el.url}</p>
          )}
        </div>
      )}

      <Row label="Opacity">
        <Slider value={[Math.round((el.opacity ?? 1) * 100)]} min={0} max={100} step={1} onPointerDown={onStart} onValueChange={(v) => u({ opacity: v[0] / 100 })} />
      </Row>
      <Row label="Blend mode">
        <select
          value={el.blendMode || "normal"}
          onChange={(e) => { onStart(); u({ blendMode: e.target.value }); }}
          data-testid="blend-mode-select"
          className="w-full h-9 rounded-lg bg-[#0E0E0E] border border-white/15 px-2.5 text-sm text-white outline-none focus:border-blue-500 cursor-pointer"
        >
          {BLEND_MODES.map((m) => (
            <option key={m.value} value={m.value} className="bg-[#161616]">{m.label}</option>
          ))}
        </select>
      </Row>
      <Row label="Rotation">
        <Slider value={[el.rotation || 0]} min={-180} max={180} step={1} onPointerDown={onStart} onValueChange={(v) => u({ rotation: v[0] })} />
      </Row>
      <div className="grid grid-cols-2 gap-3">
        <div><p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Width</p><NumberInput value={el.width} onStart={onStart} onChange={(v) => u({ width: Math.max(4, v) })} /></div>
        <div><p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Height</p><NumberInput value={el.height} onStart={onStart} onChange={(v) => u({ height: Math.max(4, v) })} /></div>
        <div><p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">X</p><NumberInput value={el.x} onStart={onStart} onChange={(v) => u({ x: v })} /></div>
        <div><p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Y</p><NumberInput value={el.y} onStart={onStart} onChange={(v) => u({ y: v })} /></div>
      </div>
    </div>
  );
}
